import { Processing } from '@paywithextend/components'
import axios from 'axios'
import React from 'react'
import { ThunkAction, ThunkDispatch } from 'redux-thunk'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import { CreateBulkReceiptAutoMatchToastMessage } from 'src/app/Toast/Toast'
import {
  BulkResponse,
  ReceiptAutoMatchPushTaskListResponse,
  ReceiptAutoMatchPushTaskStatus,
} from 'src/app/UploadReceiptsAutoMatchPopup/redux/uploadReceiptsAutoMatch.reducers'
import { connectedPush } from 'src/navigation/Lnk'
import { expenseReportUrl } from 'src/navigation/urls'
import { TASKS_QK, transactionsQueryKeys } from 'src/queries/queries.constants'
import { queryClient } from 'src/queryClient'
import {
  createSetIsCreateExpenseReportPopupOpen,
  createSetIsUploadReceiptsAutoMatchPopupOpen,
} from 'src/redux/actions/app.actions'
import { RootState } from 'src/redux/createRootReducer'
import { selectAuthedJsonApiRequestConfig } from 'src/redux/selectors/axios.selectors'
import { BackendErrorPayload } from 'src/utils/error.utils'
import { css } from 'styled-components'

export const SET_RECEIPT_AUTO_MATCH_ATTACHMENT_IDS =
  'app/receiptsAutoMatch/SET_RECEIPT_AUTO_MATCH_ATTACHMENT_IDS'
export interface SetReceiptAutoMatchAttachmentIdsAction {
  type: typeof SET_RECEIPT_AUTO_MATCH_ATTACHMENT_IDS
  payload: {
    ids: string[]
  }
}
export const createSetReceiptAutoMatchAttachmentIds = (
  ids: string[],
): SetReceiptAutoMatchAttachmentIdsAction => ({
  type: SET_RECEIPT_AUTO_MATCH_ATTACHMENT_IDS,
  payload: {
    ids,
  },
})

export const SET_IS_CREATING_EXPENSES =
  'app/receiptsAutoMatch/SET_IS_CREATING_EXPENSES'
export interface SetIsCreatingExpensesAction {
  type: typeof SET_IS_CREATING_EXPENSES
  payload: {
    isCreatingExpenses: boolean
  }
}

export const createSetIsCreatingExpenses = (
  isCreatingExpenses: boolean,
): SetIsCreatingExpensesAction => ({
  type: SET_IS_CREATING_EXPENSES,
  payload: { isCreatingExpenses },
})

export const AUTO_MATCH_RECEIPTS_RESET =
  'data/receiptAutoMatch/AUTO_MATCH_RECEIPTS_RESET'

export interface AutomatchReceiptResetAction {
  type: typeof AUTO_MATCH_RECEIPTS_RESET
}
export const createAutomatchReceiptResetAction =
  (): AutomatchReceiptResetAction => ({
    type: AUTO_MATCH_RECEIPTS_RESET,
  })

export const AUTO_MATCH_RECEIPTS_INIT =
  'app/receiptAutoMatch/AUTO_MATCH_RECEIPTS_INIT'
export const AUTO_MATCH_RECEIPTS_SUCCESS =
  'app/receiptAutoMatch/AUTO_MATCH_RECEIPTS_SUCCESS'
export const AUTO_MATCH_RECEIPTS_FAILURE =
  'app/receiptAutoMatch/AUTO_MATCH_RECEIPTS_FAILURE'

export type AutoMatchReceiptsSuccessPayload = BulkResponse

export type AutoMatchReceiptsErrorPayload = BackendErrorPayload<string>

export interface AutoMatchReceiptsInitAction {
  type: typeof AUTO_MATCH_RECEIPTS_INIT
}
export interface AutoMatchReceiptsSuccessAction {
  type: typeof AUTO_MATCH_RECEIPTS_SUCCESS
  payload: AutoMatchReceiptsSuccessPayload
}
export interface AutoMatchReceiptsFailureAction {
  type: typeof AUTO_MATCH_RECEIPTS_FAILURE
  payload: AutoMatchReceiptsErrorPayload
}

export type AutoMatchReceiptsActions =
  | AutomatchReceiptResetAction
  | AutoMatchReceiptsInitAction
  | AutoMatchReceiptsSuccessAction
  | AutoMatchReceiptsFailureAction
  | SetIsCreatingExpensesAction

const createAutoMatchReceiptsInitAction = (): AutoMatchReceiptsInitAction => ({
  type: AUTO_MATCH_RECEIPTS_INIT,
})
const createAutoMatchReceiptsSuccessAction = (
  payload: AutoMatchReceiptsSuccessPayload,
): AutoMatchReceiptsSuccessAction => ({
  type: AUTO_MATCH_RECEIPTS_SUCCESS,
  payload,
})
const createAutoMatchReceiptsFailureAction = (
  payload: AutoMatchReceiptsErrorPayload,
): AutoMatchReceiptsFailureAction => ({
  type: AUTO_MATCH_RECEIPTS_FAILURE,
  payload,
})

export const autoMatchReceipts = (
  receiptAttachmentIds: string[],
  expenseReportId?: string,
): ThunkAction<Promise<void>, RootState, void, AutoMatchReceiptsActions> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, AutoMatchReceiptsActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createAutoMatchReceiptsInitAction())
    return axios
      .post(
        `/receiptcapture/automatch`,
        {
          receiptAttachmentIds: receiptAttachmentIds,
          expenseReportId: expenseReportId,
        },
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(createAutoMatchReceiptsSuccessAction(response.data))
          dispatch({
            type: 'app/receiptsAutoMatch/SET_IS_CREATING_EXPENSES',
            payload: { isCreatingExpenses: expenseReportId !== undefined },
          })
          dispatch(
            createSetIsToastOpen(true, {
              toastMessage: (
                <CreateBulkReceiptAutoMatchToastMessage
                  isCreatingExpenses={expenseReportId !== undefined}
                />
              ),
              inProgressColorScheme: true,
              image: (
                <Processing
                  css={css`
                    transform: scale(1.2);
                  `}
                />
              ),
              bulkToast: true,
              isInfinite: true,
            }),
          )
          if (expenseReportId !== undefined) {
            dispatch(createSetIsCreateExpenseReportPopupOpen(false))
            dispatch(connectedPush(expenseReportUrl(expenseReportId)))
          } else {
            dispatch(createSetIsUploadReceiptsAutoMatchPopupOpen(false))
          }
        },
        (err) => {
          dispatch(createAutoMatchReceiptsFailureAction(err.response.data))
        },
      )
  }
}

export const getReceiptsAutoMatchBulkUploadPushStatus = (
  bulkReceiptsAutoMatchId: string,
): ThunkAction<Promise<void>, RootState, void, AutoMatchReceiptsActions> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, AutoMatchReceiptsActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createAutoMatchReceiptsInitAction())
    return axios
      .get(
        `/receiptcapture/${bulkReceiptsAutoMatchId}/status`,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          const hasNonFinalStatus = response.data.tasks.some(
            (task: ReceiptAutoMatchPushTaskListResponse) =>
              ![
                ReceiptAutoMatchPushTaskStatus.SUCCESS,
                ReceiptAutoMatchPushTaskStatus.FAILED,
              ].includes(task.status),
          )
          if (!hasNonFinalStatus) {
            queryClient.invalidateQueries({
              queryKey: transactionsQueryKeys.listScope(TASKS_QK),
            })
            // Tasks-count is always mounted — reuse any in-flight fetch instead
            // of restarting so bulk auto-match polling can't pile up requests.
            queryClient.invalidateQueries(
              { queryKey: transactionsQueryKeys.countScope(TASKS_QK) },
              { cancelRefetch: false },
            )
          }
          dispatch(createAutoMatchReceiptsSuccessAction(response.data))
        },
        (err) => {
          dispatch(createAutoMatchReceiptsFailureAction(err.response.data))
        },
      )
  }
}

export const COMPLETE_RECEIPTS_AUTO_MATCH_BULK_UPLOAD =
  'app/receiptsAutoMatch/COMPLETE_RECEIPTS_AUTO_MATCH_BULK_UPLOAD'
export interface CompleteReceiptsAutoMatchBulkUploadAction {
  type: typeof COMPLETE_RECEIPTS_AUTO_MATCH_BULK_UPLOAD
}
export const createCompleteReceiptsAutoMatchBulkUpload =
  (): CompleteReceiptsAutoMatchBulkUploadAction => ({
    type: COMPLETE_RECEIPTS_AUTO_MATCH_BULK_UPLOAD,
  })

export const AUTO_MATCH_RECEIPTS_ENABLED_INIT =
  'app/receiptAutoMatch/AUTO_MATCH_RECEIPTS_ENABLED_INIT'
export const AUTO_MATCH_RECEIPTS_ENABLED_SUCCESS =
  'app/receiptAutoMatch/AUTO_MATCH_RECEIPTS_ENABLED_SUCCESS'
export const AUTO_MATCH_RECEIPTS_ENABLED_FAILURE =
  'app/receiptAutoMatch/AUTO_MATCH_RECEIPTS_ENABLED_FAILURE'

export type AutoMatchReceiptsEnabledSuccessPayload = {
  receiptCaptureEnabled: boolean
}

export type AutoMatchReceiptsEnabledErrorPayload = BackendErrorPayload<string>

export interface AutoMatchReceiptsEnabledInitAction {
  type: typeof AUTO_MATCH_RECEIPTS_ENABLED_INIT
}
export interface AutoMatchReceiptsEnabledSuccessAction {
  type: typeof AUTO_MATCH_RECEIPTS_ENABLED_SUCCESS
  payload: AutoMatchReceiptsEnabledSuccessPayload
}
export interface AutoMatchReceiptsEnabledFailureAction {
  type: typeof AUTO_MATCH_RECEIPTS_ENABLED_FAILURE
  payload: AutoMatchReceiptsEnabledErrorPayload
}

export type AutoMatchReceiptsEnabledActions =
  | AutoMatchReceiptsEnabledInitAction
  | AutoMatchReceiptsEnabledSuccessAction
  | AutoMatchReceiptsEnabledFailureAction

const createAutoMatchReceiptsEnabledInitAction =
  (): AutoMatchReceiptsEnabledInitAction => ({
    type: AUTO_MATCH_RECEIPTS_ENABLED_INIT,
  })
const createAutoMatchReceiptsEnabledSuccessAction = (
  payload: AutoMatchReceiptsEnabledSuccessPayload,
): AutoMatchReceiptsEnabledSuccessAction => ({
  type: AUTO_MATCH_RECEIPTS_ENABLED_SUCCESS,
  payload,
})
const createAutoMatchReceiptsEnabledFailureAction = (
  payload: AutoMatchReceiptsEnabledErrorPayload,
): AutoMatchReceiptsEnabledFailureAction => ({
  type: AUTO_MATCH_RECEIPTS_ENABLED_FAILURE,
  payload,
})

export const getAutoMatchReceiptsEnabled = (
  id: string,
): ThunkAction<
  Promise<void>,
  RootState,
  void,
  AutoMatchReceiptsEnabledActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, AutoMatchReceiptsEnabledActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createAutoMatchReceiptsEnabledInitAction())
    return axios
      .get(
        `/receiptcapture/${id}/enabled`,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(createAutoMatchReceiptsEnabledSuccessAction(response.data))
        },
        (err) => {
          dispatch(createAutoMatchReceiptsEnabledFailureAction(err))
        },
      )
  }
}
